import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';

interface Friend {
  id: string;
  username: string;
  display_name: string;
  is_online: boolean;
}

interface OnlineNotification {
  friendId: string;
  friendName: string;
  timestamp: number;
}

/**
 * Hook to detect when friends come online and notify
 *
 * OPTIMIZATION: Removed 30s polling to reduce API calls and KV writes
 * This hook now only does ONE initial fetch to get friends list.
 * Real-time online status updates should be handled via InfiniChat WebSocket.
 */
export function useFriendOnlineNotifications(_onFriendOnline: (notification: OnlineNotification) => void) {
  const { token } = useAuthStore();
  const [friends, setFriends] = useState<Map<string, boolean>>(new Map());
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch once per session
    if (!token || hasFetchedRef.current) return;

    const fetchFriendsOnce = async () => {
      try {
        hasFetchedRef.current = true;
        const response = await fetch(
          'https://mkey-api-production.fidelismadboat.workers.dev/friendships?status=accepted',
          {
            headers: { 'Authorization': `Bearer ${token}` },
          }
        );

        if (!response.ok) return;

        const data = await response.json();
        const friendsList: Friend[] = data.friendships?.map((f: any) => f.friend) || [];

        // Create status map from initial fetch
        const statusMap = new Map<string, boolean>();
        friendsList.forEach(friend => {
          statusMap.set(friend.id, friend.is_online);
        });
        setFriends(statusMap);

        console.log('[FriendNotif] Initial friends loaded:', friendsList.length);
      } catch (error) {
        console.error('[FriendNotif] Error fetching friends:', error);
        hasFetchedRef.current = false; // Allow retry on error
      }
    };

    fetchFriendsOnce();
  }, [token]);

  // Note: _onFriendOnline callback is available for future WebSocket integration
  // When InfiniChat receives presence updates, it can call this callback

  return { friends };
}
